import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import { format, parse } from 'date-fns'

import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import SaveIcon from '@material-ui/icons/Save'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Menu, MenuItem, IconButton, InputAdornment, Select, FormControl, InputLabel } from '@material-ui/core'
import LinearProgress from '@material-ui/core/LinearProgress'
import Alert from '@material-ui/lab/Alert'


import { useFarms, useFarm } from '@graniot/common/adapters/farms'
import { useAuth } from '@graniot/common/context/AuthContext'
import EditParcelMap from './EditParcelMap'
import { LayerSelector } from '../LayerSelector'

import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import Container from '@material-ui/core/Container'
import Tooltip from '@material-ui/core/Tooltip'
import { MoreVert as MoreVertIcon } from '@material-ui/icons';


import { useConverToGeojson, useDeleteParcel } from '../../../../adapters/addparcels'
import { useUpdateParcelMutation } from '../../../../adapters/parcels'
import { useSatelliteImages } from '../../../../context/SatelliteImagesContext'
import { useMetadataVisibility } from '../../../../context/MetadataVisibilityContext'
import { DeleteDialog } from './DeleteDialog'

const useStyles = makeStyles((theme) => ({
  tab: {
    [theme.breakpoints.down('sm')]: {},
    [theme.breakpoints.up('md')]: {
      marginLeft: 100,
      marginRight: 100,
    },
    [theme.breakpoints.up('lg')]: {
      marginLeft: 250,
      marginRight: 250,
    },
  },
  margin: {
    marginLeft: 10,
    marginRight: 10
  },
  statusContainer: {
    marginBottom: theme.spacing(2)
  },
  parcelNameInput: {
    fontSize: '1.25rem',
    fontWeight: 500,
    color: '#232C57',
  },
  attributes: {
    backgroundColor: '#F7F8FA',
    border: '1px solid rgba(35,44,87,0.10)',
    borderRadius: 12,
    padding: theme.spacing(1.5, 1.5, 1),
  },
  attributesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  attributesHint: {
    color: 'rgba(35,44,87,0.6)',
    margin: theme.spacing(0.5, 0, 1),
  },
  // toggle | name | value | delete, so every row lines up in columns
  attributeRow: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr 1fr 32px',
    alignItems: 'center',
    columnGap: theme.spacing(1),
  },
  attributeColumnLabel: {
    fontSize: '0.75rem',
    color: 'rgba(35,44,87,0.6)',
  },
}))

function EditParcel({
  layers,
  parcel,
  setSelectedParcel,
  width,
  dispatchFarms,
  handleClose,
  farms,
  refetchFarms,
  dispatchAddedParcels,
  editParcel,
  selectedResolution,
  farmsHectares,
  isLoadingParcelFISHistory,
  dataParcelFISHistory,
  refetchParcelFISHistory,
  selectedLayer,
  handleSelectedLayer,
  from_date,
  selectedFarm,
  setIsUpdating,
}) {
  const classes = useStyles()
  const { t } = useTranslation()

  const { loggedUser, user, isClientPaying, isTecnician, isCompanyAdmin } = useAuth()
  const { isKeyVisible, toggleKey } = useMetadataVisibility()
  const { data, refetch: refetchFarmData, isSuccess: isSuccessFarmRefetch, isLoading: isLoadingFarmData } = useFarm(user, selectedFarm.id)
  const showSatelliteLayer = selectedResolution?.resolution !== 'DEFORESTATION'
  const [name, setName] = React.useState(parcel.properties.name)
  const [selectedLayerDate, setSelectedLayerDate] = React.useState(null)

  const [featureStats, setFeatureStats] = React.useState(null)

  const [openSnackbar, setOpenSnackbar] = React.useState(null)
  const [statusSnackbar, setStatusSnackbar] = React.useState(null)
  const [messageSnackbar, setMessageSnackbar] = React.useState(null)

  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false)
  // state to handle metadata
  const [metadataParcel, setMetadataParcel] = React.useState(null)
  
  // Use a ref to store the original handleClose function
  const handleCloseRef = React.useRef(handleClose);
  
  // Keep the original handleClose reference updated
  React.useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  const [parcelGeoJson, setParcelGeoJson] = React.useState({
    type: 'FeatureCollection',
    features: [parcel],
  })

  const [selectedIndexLayer, setSelectedIndexLayer] = React.useState(false)

  const { satelliteImages, dispatchSatelliteImages } = useSatelliteImages()

  const [updateButtonDisabled, setUpdateButtonDisabled] =
    React.useState(false)

  const deleteParcelMutation = useDeleteParcel()
  const updateParcel = useUpdateParcelMutation()
  const [anchorEl, setAnchorEl] = React.useState(null);
  
  // Create a wrapped version of handleClose that checks if update is in progress
  const safeHandleClose = React.useCallback(() => {
    if (updateParcel.isLoading) {
      setOpenSnackbar(true);
      setStatusSnackbar('warning');
      setMessageSnackbar(t('parcel_update_in_progress'));
      return;
    }
    
    // Safe to close, call the original handleClose
    if (handleCloseRef.current) {
      handleCloseRef.current();
    }
  }, [updateParcel.isLoading, t]);

  const handleClick = (event) => {
    if (isTecnician() || isCompanyAdmin()) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };


  const flattenMetadata = (geojson) => {
    const cloned = JSON.parse(JSON.stringify(geojson));
    if (cloned.features) {
      cloned.features.forEach((feature) => {
        const props = feature.properties;
        if (props && props.metadata && typeof props.metadata === 'object') {
          Object.entries(props.metadata).forEach(([key, value]) => {
            const flatKey = key in props ? `metadata_${key}` : key;
            props[flatKey] = value;
          });
          delete props.metadata;
        }
      });
    }
    return cloned;
  };

  const handleDownload = (name, data) => {
    if (isTecnician() || isCompanyAdmin()) {
      const flattened = flattenMetadata(data);
      const geoJsonString = JSON.stringify(flattened);

      // Create a Blob with the GeoJson string
      const blob = new Blob([geoJsonString], { type: 'application/json' });

      // Create a download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name + '.geojson';

      // Simulate a click to trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  }


  const handleDownloadParcel = () => {
    if (isTecnician() || isCompanyAdmin()) {
      handleDownload(parcelGeoJson.features[0].properties.name, parcelGeoJson)
    }
  };

  React.useEffect(() => {
    if (data)
      dispatchFarms({ type: 'UPDATE_FARM_NAME', payload: { editFarm: data } })
  }, [data])

  const handleDownloadFarm = () => {
    if (isTecnician() || isCompanyAdmin()) {
      let farm = farms.find((farm) => farm.checked)
      handleDownload(farm?.name, farm?.farm_parcels)
    }
  };


  React.useEffect(async () => {
    const handleDeleteSuccess = async () => {
      if (deleteParcelMutation.isSuccess) {
        dispatchFarms({ type: 'REMOVE_PARCEL', parcel: parcel });
        deleteParcelMutation.reset();
        await refetchFarmData();
        // Use the safe handle close here too
        safeHandleClose();
      }
    };

    handleDeleteSuccess();
  }, [deleteParcelMutation, dispatchFarms, parcel, safeHandleClose]);


  React.useEffect(async () => {
    if (updateParcel.isSuccess) {
      // Set isUpdating to false immediately
      setIsUpdating(false)
      
      // Update success message
      setOpenSnackbar(true)
      setStatusSnackbar('success')
      setMessageSnackbar(t('parcel_updated_successfully'))
      
      // Then perform the rest of the operations
      await refetchFarmData()
      
      const currentFarm = farms.find((farm) => farm.checked);
      
      if (updateParcel.data.length == 2 && Array.isArray(updateParcel.data)) {
        // Instead of adding parcels and then removing the old one in two separate actions,
        // use a single REPLACE_FARM_PARCELS action to update all at once
        
        // Create a new features array without the old parcel but with the new ones
        const updatedFeatures = [
          ...currentFarm.farm_parcels.features.filter(p => p.id !== parcel.id),
          ...updateParcel.data
        ];
        
        // Update the entire features collection in one go
        dispatchFarms({
          type: 'REPLACE_FARM_PARCELS',
          farm: currentFarm,
          features: updatedFeatures
        });
        
        setSelectedParcel(updateParcel.data[0])
      } else if (
        typeof updateParcel.data === 'object' &&
        !Array.isArray(updateParcel.data)
      ) {
        dispatchSatelliteImages({
          type: 'DELETE_IMAGES',
          payload: updateParcel.data,
        })
        dispatchFarms({
          type: 'UPDATE_PARCEL',
          parcel: updateParcel.data,
          name,
        })
        setSelectedParcel(updateParcel.data)
      }

      // Don't reset immediately to allow the success message to be visible
      // Wait 3 seconds before resetting to clear the success message
      setTimeout(() => {
        updateParcel.reset()
      }, 3000)
    }
  }, [updateParcel.isSuccess])

  // Add effect to handle error state 
  React.useEffect(() => {
    if (updateParcel.isError) {
      // Set isUpdating to false immediately
      setIsUpdating(false)
      
      setOpenSnackbar(true)
      setStatusSnackbar('error')
      const errorMessage = updateParcel.error?.message || t('error_updating_parcel')
      setMessageSnackbar(errorMessage)
      
      // Don't reset immediately to allow the error message to be visible
      // Wait 3 seconds before resetting to clear the error message
      setTimeout(() => {
        updateParcel.reset()
      }, 3000)
    }
  }, [updateParcel.isError])

  const isMetadataIncomplete = () => {
    let incomplete = false
    Object.entries(metadataParcel).map(([key, value]) => {
      if (key === '' || value === '') {
        incomplete = true
      }
    })
    return incomplete
  }

  const handleUpdateParcel = () => {
    // Reset any previous success/error states
    updateParcel.reset();
    
    // check if name is empty
    if (metadataParcel) {
      if (name === '') {
        setOpenSnackbar(true)
        setStatusSnackbar('error')
        setMessageSnackbar(t('name parcel empty'))
        return;
      } else if (isMetadataIncomplete()) {
        setOpenSnackbar(true)
        setStatusSnackbar('error')
        setMessageSnackbar(t('metadata_empty'))
        return;
      }
    }
    // Show update in progress message
    setIsUpdating(true)
    setOpenSnackbar(true)
    setStatusSnackbar('info')
    setMessageSnackbar(t('updating_parcel_wait'))

    // Prepare metadata including geometry_type for company ID 400
    let finalMetadata = metadataParcel
    if (loggedUser?.company?.id === 400) {
      finalMetadata = { ...metadataParcel }
    }

    updateParcel.mutate({ id: parcel.id, name, parcelGeoJson, metadata: finalMetadata })
  }

  const handleDeleteParcel = () => {
    deleteParcelMutation.mutate(parcel)
  }

  const disableNotAvalibleSentinelImages = (date) => {
    return indexOfDate(date) === -1
  }

  const showLayerDate = (value) => {
    setSelectedIndexLayer(value)
  }

  React.useEffect(() => {
    if (dataParcelFISHistory) {
      let feature_stats = Object.assign([], dataParcelFISHistory)
      feature_stats.reverse()
      setFeatureStats(feature_stats)
    }
  }, [dataParcelFISHistory])

  const indexOfDate = (date) => {
    return featureStats
      .map((d) => format(new Date(d.date), 'dd/MM/yyyy'))
      .indexOf(date)
  }

  function indexOfDate2(date) {
    return featureStats.findIndex((d) => {
      return new Date(d.date) - parse(date, 'dd/MM/yyyy', new Date()) > 0
    })
  }

  const handleChangeComparedDate = (comparedDate) => {
    setSelectedLayerDate(featureStats[indexOfDate2(comparedDate)].date)
  }


  React.useEffect(() => {
    if (featureStats && featureStats.length > 1) {
      setSelectedLayerDate(featureStats[featureStats.length - 1].date)
    }
  }, [featureStats])

  // useEffect to fill metadata when page is loaded
  React.useEffect(() => {
    if (parcel.properties.metadata !== null) {
      let metadataLocal = {}
      Object.entries(parcel.properties.metadata).forEach(([key, value]) => {
        if (key !== 'cover_loss_percentile' && key !== 'geometry_type') {
          metadataLocal = { ...metadataLocal, [key]: value };
        }
      });
      setMetadataParcel(metadataLocal)
    }
  }, [])

  const addMetadata = () => {
    if (isTecnician() || isCompanyAdmin()) {
      if (metadataParcel === null) {
        setMetadataParcel({ '': '' })
      } else {
        setMetadataParcel({ ...metadataParcel, '': '' })
      }
    }
  }

  const removeMetadata = (key) => {
    if (isTecnician() || isCompanyAdmin()) {
      let metadataLocal = metadataParcel
      delete metadataLocal[key]
      setMetadataParcel({ ...metadataLocal })
    }
  }

  const handleCloseDialog = () => {
    if (updateParcel.isLoading) {
      console.log("UPDATE PARCEL IS LOADING")
      setOpenSnackbar(true);
      setStatusSnackbar('warning');
      setMessageSnackbar(t('parcel_update_in_progress'));
      return;
    }
    setOpenDeleteDialog(false)
  }

  const confirmDelete = () => {
    handleDeleteParcel()
  }

  // Add useEffect to handle ESC key blocking
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Prevent ESC key from closing the dialog while updating
      if (event.key === "Escape" && updateParcel.isLoading) {
        event.preventDefault();
        event.stopPropagation();
        
        // Show a notification that closing is not allowed during update
        setOpenSnackbar(true);
        setStatusSnackbar('warning');
        setMessageSnackbar(t('parcel_update_in_progress'));
      }
    };

    // Add event listener to the document
    document.addEventListener("keydown", handleKeyDown, true);

    // Clean up when component unmounts
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [updateParcel.isLoading, t]);

  return (
    <Container style={{ marginTop: 30 }}>
      {openDeleteDialog && <DeleteDialog 
        handleClose={handleCloseDialog} 
        confirmDelete={confirmDelete} 
        isLoading={deleteParcelMutation.isLoading || updateParcel.isLoading} 
      />}
      <Grid container spacing={4}>
        <Grid item xs={12} sm={4}>
          <Grid
            container
            direction="row"
            style={{ marginBottom: 20 }}
            spacing={3}
          >
            <Grid item xs={12}>
              <Box display="flex" alignItems="flex-end">
                <TextField
                  label={t('parcel_name')}
                  name="name"
                  value={name}
                  onChange={(event) =>
                    (isTecnician() || isCompanyAdmin()) ? setName(event.target.value) : null
                  }
                  fullWidth
                  disabled={!(isTecnician() || isCompanyAdmin()) || updateParcel.isLoading}
                  InputProps={{ classes: { input: classes.parcelNameInput } }}
                />
                <>
                  {(isTecnician() || isCompanyAdmin()) && (
                    <IconButton 
                      aria-label="more" 
                      aria-controls="long-menu" 
                      aria-haspopup="true" 
                      onClick={handleClick}
                      disabled={updateParcel.isLoading}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                  <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                  >
                    <MenuItem onClick={handleDownloadParcel}>{t('download_parcel')}</MenuItem>
                    <MenuItem onClick={handleDownloadFarm}>{t('download_farm')}</MenuItem>
                  </Menu>
                </>
              </Box>
            </Grid>


              <Grid item xs={12}>
                {((metadataParcel && Object.keys(metadataParcel).length > 0) || isTecnician() || isCompanyAdmin()) && (
                <Box className={classes.attributes}>
                <Box className={classes.attributesHeader}>
                  <Typography variant="subtitle2">
                    {t('metadata')}
                  </Typography>
                  {(isTecnician() || isCompanyAdmin()) && (
                    <Button
                      color="primary"
                      size='small'
                      onClick={() => { addMetadata() }}
                      startIcon={
                        <AddIcon />
                      }
                      disabled={updateParcel.isLoading}
                    >
                      {t('add_row')}
                    </Button>
                  )}
                </Box>

                {!metadataParcel || Object.keys(metadataParcel).length === 0 ? (
                  (isTecnician() || isCompanyAdmin()) && (
                    <Typography variant="body2" className={classes.attributesHint}>
                      {t('parcel_attributes_hint')}
                    </Typography>
                  )
                ) : (
                  <Box className={classes.attributeRow}>
                    <span />
                    <Typography className={classes.attributeColumnLabel}>{t('name')}</Typography>
                    <Typography className={classes.attributeColumnLabel}>{t('value')}</Typography>
                    <span />
                  </Box>
                )}

                { metadataParcel !== null && (
                Object.entries(metadataParcel).map(([key, value], index) => (
                  <Box className={classes.attributeRow} key={index}>
                    <Tooltip title={key === '' ? '' : isKeyVisible(key) ? t('hide_in_tooltip') : t('show_in_tooltip')}>
                      <span>
                        <IconButton
                          size='small'
                          color={isKeyVisible(key) ? 'primary' : 'default'}
                          onClick={() => toggleKey(key)}
                          disabled={key === ''}
                          aria-label={isKeyVisible(key) ? t('hide_in_tooltip') : t('show_in_tooltip')}
                        >
                          {isKeyVisible(key) ? <VisibilityIcon fontSize='small' /> : <VisibilityOffIcon fontSize='small' />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <TextField
                      placeholder={t('attribute_name_example')}
                      inputProps={{ 'aria-label': t('name') }}
                      onChange={(e) => {
                        if (isTecnician() || isCompanyAdmin()) {
                          const newKey = e.target.value;
                          const updatedMetadata = { ...metadataParcel };
                          const newPosition = Object.keys(metadataParcel).indexOf(key);
                          delete updatedMetadata[key];
                          const newMetadata = Object.entries(updatedMetadata);
                          newMetadata.splice(newPosition, 0, [newKey, value]);
                          const updatedParcel = Object.fromEntries(newMetadata);
                          setMetadataParcel(updatedParcel);
                        }
                      }}
                      value={key}
                      size="small"
                      disabled={!(isTecnician() || isCompanyAdmin()) || updateParcel.isLoading}
                    />

                    <TextField
                      placeholder={t('attribute_value_example')}
                      inputProps={{ 'aria-label': t('value') }}
                      onChange={(e) => {
                        if (isTecnician() || isCompanyAdmin()) {
                          const newValue = e.target.value;
                          const updatedMetadata = { ...metadataParcel, [key]: newValue };
                          setMetadataParcel(updatedMetadata);
                        }
       }}
                      value={value}
                      size="small"
                      disabled={!(isTecnician() || isCompanyAdmin()) || updateParcel.isLoading}
                    />

                    {(isTecnician() || isCompanyAdmin()) && (
                      <IconButton
                        color="secondary"
                        size='small'
                        onClick={() => { removeMetadata(key) }}
                        disabled={updateParcel.isLoading}
                        aria-label={t('delete_attribute')}
                      >
                        <DeleteIcon fontSize='inherit' />
                      </IconButton>
                    )}
                  </Box>
                ))
              )}
                </Box>
                )}
              </Grid>


            {(isTecnician() || isCompanyAdmin()) &&
            <>
            <Grid item xs={12}>
              {/* Status indicator area */}
              <Box className={classes.statusContainer}>
                {updateParcel.isLoading && (
                  <>
                    <LinearProgress color="primary" />
                    <Box mt={1}>
                      <Alert severity="info">{t('updating_parcel_wait')}</Alert>
                    </Box>
                  </>
                )}
                {updateParcel.isError && !updateParcel.isLoading && (
                  <Alert severity="error">{updateParcel.error?.message || t('error_updating_parcel')}</Alert>
                )}
                {updateParcel.isSuccess && !updateParcel.isLoading && (
                  <Alert severity="success">{t('parcel_updated_successfully')}</Alert>
                )}
              </Box>
              <Box
                display="flex"
                justifyContent={
                  isWidthUp('sm', width) ? 'end' : 'center'
                }
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={
                    updateParcel.isLoading ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                      />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleUpdateParcel}
                  fullWidth
                  disabled={updateButtonDisabled || updateParcel.isLoading}
                >
                  {updateParcel.isLoading ? t('updating') : t('update')}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent={
                  isWidthUp('sm', width) ? 'end' : 'center'
                }
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => { setOpenDeleteDialog(true) }}
                  fullWidth
                  startIcon={
                    <DeleteIcon />
                  }
                  disabled={updateParcel.isLoading}
                >
                  {t('delete-parcel')}
                </Button>
              </Box>
            </Grid>
            </>}
          </Grid>
        </Grid>

        <Grid item xs={12} sm={8}>
          <Grid item xs={12} style={{ marginBottom: 15 }}>
            <Paper>
              <Box display="flex">
                <Box flexGrow={1}>
                  <EditParcelMap
                    farms={farms}
                    parcel={parcel}
                    layers={layers}
                    dispatchAddedParcels={dispatchAddedParcels}
                    parcelGeoJson={parcelGeoJson}
                    setParcelGeoJson={setParcelGeoJson}
                    editParcel={editParcel}
                    setUpdateButtonDisabled={
                      setUpdateButtonDisabled
                    }
                    selectedResolution={selectedResolution}
                    layer={showSatelliteLayer ? selectedLayer : null}
                    selectedLayerDate={selectedLayerDate}
                    farmsHectares={farmsHectares}
                    showLayerDate={showLayerDate}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
          </Grid>
          <Box display='flex'>
            {/* Only while the map shows the index layer, like the layer date next to it */}
            {showSatelliteLayer && selectedLayer && selectedIndexLayer && <LayerSelector
              layers={layers}
              selectedLayer={selectedLayer}
              handleSelectedLayer={handleSelectedLayer}
              width={'100%'}
              className={classes.margin}
            />}

            {showSatelliteLayer && selectedIndexLayer &&
              (selectedLayerDate ? (
                <KeyboardDatePicker
                  label={t('Layer date')}
                  name="selected_layer_date"
                  value={selectedLayerDate}
                  onChange={(date) =>
                    handleChangeComparedDate(
                      format(date, 'dd/MM/yyyy')
                    )
                  }
                  shouldDisableDate={(date) =>
                    disableNotAvalibleSentinelImages(
                      format(date, 'dd/MM/yyyy')
                    )
                  }
                  maxDate={new Date()}
                  minDate={new Date(from_date)}
                  loading={true}
                  format="dd/MM/yyyy"
                  variant="inline"
                  fullWidth
                  autoOk
                  className={classes.margin}
                />
              ) : (
                selectedLayer && <CircularProgress size={24} />
              )
              )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}

export default withWidth()(EditParcel)
